import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Clock, BookOpen,
  CheckCircle, AlertCircle, HelpCircle, Award, ArrowLeft,
  Volume2, VolumeX
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import {
  getPlChapterPagesInfo, getPlChapterPage, savePlPageProgress,
  updatePlChapterTime, getPlQuiz, submitPlQuiz, completePlChapterNoQuiz
} from '@/services/api';
import { toast } from 'sonner';

const PreLicensingChapterPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [pageInfo, setPageInfo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeSpent, setTimeSpent] = useState(0);

  // Quiz state
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizData, setQuizData] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizResult, setQuizResult] = useState(null);

  // Narrator state
  const [isNarrating, setIsNarrating] = useState(false);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [selectedVoice] = useState('onyx');
  const audioRef = useRef(null);
  const speechRef = useRef(null);

  // Page read timer - enforces minimum read time per page
  const [pageTimeRemaining, setPageTimeRemaining] = useState(0);
  const pageTimerRef = useRef(null);

  // Admins bypass page timer for content review/editing
  const isAdmin = user?.role === 'admin';
  const timerLocked = pageTimeRemaining > 0 && !isAdmin;

  const timerRef = useRef(null);
  const syncTimerRef = useRef(null);
  const serverTimeRef = useRef(0);
  const lastSyncedTimeRef = useRef(0);
  const timeSpentRef = useRef(0);

  useEffect(() => { timeSpentRef.current = timeSpent; }, [timeSpent]);

  const syncTimeToServer = useCallback(async () => {
    const totalTime = serverTimeRef.current + timeSpentRef.current;
    if (totalTime > lastSyncedTimeRef.current && id) {
      try {
        await updatePlChapterTime(id, totalTime);
        lastSyncedTimeRef.current = totalTime;
      } catch {}
    }
  }, [id]);

  // Timer
  useEffect(() => {
    timerRef.current = setInterval(() => setTimeSpent(prev => prev + 1), 1000);
    syncTimerRef.current = setInterval(syncTimeToServer, 30000);
    return () => {
      clearInterval(timerRef.current);
      clearInterval(syncTimerRef.current);
      syncTimeToServer();
      if (pageTimerRef.current) clearInterval(pageTimerRef.current);
      if (speechRef.current) window.speechSynthesis.cancel();
      if (audioRef.current) audioRef.current.pause();
    };
  }, [syncTimeToServer]);

  // Load chapter info
  useEffect(() => {
    const loadChapter = async () => {
      try {
        setLoading(true);
        const infoRes = await getPlChapterPagesInfo(id);
        setPageInfo(infoRes.data);
        const startPage = infoRes.data.resume_page || 1;
        setCurrentPage(startPage);
        const pageRes = await getPlChapterPage(id, startPage);
        setPageContent(pageRes.data);
        
        // Start page read timer for initial page
        if (pageTimerRef.current) clearInterval(pageTimerRef.current);
        const minSeconds = pageRes.data.min_read_seconds || 30;
        setPageTimeRemaining(minSeconds);
        pageTimerRef.current = setInterval(() => {
          setPageTimeRemaining(prev => {
            if (prev <= 1) {
              clearInterval(pageTimerRef.current);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load chapter');
        toast.error('Failed to load chapter content');
      } finally {
        setLoading(false);
      }
    };
    if (id) loadChapter();
  }, [id]);

  const goToPage = async (page) => {
    try {
      // Stop narrator on page change
      if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
      window.speechSynthesis.cancel();
      setIsNarrating(false);
      const res = await getPlChapterPage(id, page);
      setPageContent(res.data);
      setCurrentPage(page);
      savePlPageProgress(id, page).catch((err) => {
        if (err?.response?.status === 403) {
          toast.error(err.response.data.detail || 'Please wait before proceeding');
        }
      });
      
      // Start page read timer
      if (pageTimerRef.current) clearInterval(pageTimerRef.current);
      const minSeconds = res.data.min_read_seconds || 30;
      setPageTimeRemaining(minSeconds);
      pageTimerRef.current = setInterval(() => {
        setPageTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(pageTimerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      toast.error('Failed to load page');
    }
  };

  const handleStartQuiz = async () => {
    try {
      const res = await getPlQuiz(id);
      setQuizData(res.data);
      setShowQuiz(true);
      setQuizAnswers({});
      setQuizSubmitted(false);
      setQuizResult(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      toast.error('Failed to load quiz');
    }
  };

  const handleSubmitQuiz = async () => {
    if (!quizData) return;
    const unanswered = quizData.questions.filter(q => !quizAnswers[q.number]);
    if (unanswered.length > 0) {
      toast.error(`Please answer all questions. ${unanswered.length} remaining.`);
      return;
    }
    try {
      const res = await submitPlQuiz(id, quizAnswers, timeSpent);
      setQuizResult(res.data);
      setQuizSubmitted(true);
      if (res.data.passed) {
        toast.success(`Quiz passed! ${res.data.score}/${res.data.total} correct.`);
      } else {
        toast.error(`Quiz not passed. ${res.data.score}/${res.data.total}. Need 75%.`);
      }
    } catch {
      toast.error('Failed to submit quiz');
    }
  };

  // Narrator - OpenAI TTS
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleNarrate = async () => {
    if (isNarrating) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      window.speechSynthesis.cancel();
      setIsNarrating(false);
      return;
    }

    const contentDiv = document.querySelector('.chapter-content');
    if (!contentDiv) return;

    const textContent = contentDiv.innerText;
    const truncatedText = textContent.slice(0, 4096);

    if (truncatedText.length < 10) {
      toast.error('Not enough text content to narrate');
      return;
    }

    setIsLoadingAudio(true);

    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tts/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          text: truncatedText,
          voice: selectedVoice,
          speed: 1.0
        })
      });

      if (!response.ok) throw new Error('TTS generation failed');

      const data = await response.json();

      if (data.audio_base64) {
        const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
        audioRef.current = audio;
        audio.onended = () => setIsNarrating(false);
        audio.onerror = () => {
          setIsNarrating(false);
          toast.error('Audio playback error');
        };
        await audio.play();
        setIsNarrating(true);
        toast.success('AI Narrator started');
      }
    } catch (error) {
      console.error('TTS error:', error);
      toast.info('Using browser narrator (AI voice unavailable)');
      fallbackToWebSpeech(textContent);
    } finally {
      setIsLoadingAudio(false);
    }
  };

  const fallbackToWebSpeech = (text) => {
    const utterance = new SpeechSynthesisUtterance(text.slice(0, 5000));
    utterance.rate = 0.9;
    utterance.pitch = 1;
    const voices = window.speechSynthesis.getVoices();
    const preferredVoice = voices.find(v => v.name.includes('Daniel') || v.name.includes('Google US English'));
    if (preferredVoice) utterance.voice = preferredVoice;
    utterance.onend = () => setIsNarrating(false);
    utterance.onerror = () => setIsNarrating(false);
    speechRef.current = utterance;
    window.speechSynthesis.speak(utterance);
    setIsNarrating(true);
  };

  const formatTime = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary">Loading chapter...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-lg mb-4">{error}</p>
          <Button onClick={() => navigate('/pre-licensing/dashboard')}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  const totalPages = pageInfo?.total_pages || 1;
  const isLastPage = currentPage >= totalPages;

  // ======== Quiz View ========
  if (showQuiz && quizData) {
    return (
      <div className="min-h-screen bg-background" data-testid="pl-quiz-view">
        <div className="max-w-3xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between mb-6">
            <button onClick={() => setShowQuiz(false)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm">
              <ArrowLeft className="w-4 h-4" /> Back to Chapter
            </button>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="w-4 h-4" /> {formatTime(timeSpent)}
            </div>
          </div>

          <h1 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Barlow Condensed' }}>{quizData.title}</h1>
          <p className="text-sm text-muted-foreground mb-8">{quizData.subtitle}</p>

          {/* Focused Remediation — only renders after submit, only if there are missed questions */}
          {quizSubmitted && (() => {
            const missed = quizData.questions.filter(q => quizAnswers[q.number] !== q.correct_answer);
            if (missed.length === 0) return null;
            return (
              <div className="mb-8 p-5 bg-amber-500/10 border-l-4 border-amber-500 rounded" data-testid="pl-quiz-remediation">
                <h3 className="text-lg font-bold mb-2 text-amber-500 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  Questions to Review ({missed.length})
                </h3>
                <p className="text-sm text-muted-foreground mb-5">
                  These questions were answered incorrectly. The correct answer and explanation appear below. The complete review of every quiz question follows further down the page.
                </p>
                <div className="space-y-4">
                  {missed.map((q) => {
                    const userAnswer = quizAnswers[q.number];
                    const correctOption = q.options.find(o => o.letter === q.correct_answer);
                    const userOption = q.options.find(o => o.letter === userAnswer);
                    return (
                      <div key={`miss-${q.number}`} className="p-4 bg-card border border-amber-500/30 rounded-lg" data-testid={`pl-missed-q-${q.number}`}>
                        <p className="font-medium mb-3 text-sm">
                          <span className="text-amber-500 mr-2">{q.number}.</span>{q.question}
                        </p>
                        <div className="space-y-2 text-sm">
                          <div>
                            <span className="text-muted-foreground">Your answer: </span>
                            <span className="text-red-500 font-medium">
                              {userOption ? `${userOption.letter}. ${userOption.text}` : 'Not answered'}
                            </span>
                          </div>
                          {correctOption && (
                            <div className="p-3 bg-green-500/10 border border-green-500/30 rounded">
                              <div className="text-green-500 font-semibold text-xs mb-1">CORRECT ANSWER</div>
                              <div>{correctOption.letter}. {correctOption.text}</div>
                            </div>
                          )}
                          {q.explanation && (
                            <div className="p-3 bg-muted/50 rounded">
                              <div className="text-xs font-semibold text-muted-foreground mb-1">WHY THIS IS THE ANSWER</div>
                              <div className="text-muted-foreground">{q.explanation}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          <div className="space-y-8">
            {quizData.questions.map((q) => {
              const userAnswer = quizAnswers[q.number];
              const isCorrect = quizSubmitted && userAnswer === q.correct_answer;
              const isWrong = quizSubmitted && userAnswer && userAnswer !== q.correct_answer;

              return (
                <div key={q.number} className={`bg-card border rounded-xl p-6 ${
                  quizSubmitted ? (isCorrect ? 'border-green-500/50' : isWrong ? 'border-red-500/50' : 'border-border') : 'border-border'
                }`} data-testid={`pl-quiz-q-${q.number}`}>
                  <p className="font-medium mb-4">
                    <span className="text-amber-500 mr-2">{q.number}.</span>
                    {q.question}
                  </p>

                  <RadioGroup
                    value={userAnswer || ''}
                    onValueChange={(val) => !quizSubmitted && setQuizAnswers(prev => ({ ...prev, [q.number]: val }))}
                    className="space-y-3"
                  >
                    {q.options.map((opt) => {
                      const isSelected = userAnswer === opt.letter;
                      const optCorrect = quizSubmitted && opt.letter === q.correct_answer;
                      const optWrong = quizSubmitted && isSelected && opt.letter !== q.correct_answer;

                      return (
                        <div key={opt.letter} className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                          optCorrect ? 'border-green-500/50 bg-green-500/5' :
                          optWrong ? 'border-red-500/50 bg-red-500/5' :
                          isSelected ? 'border-amber-500/50 bg-amber-500/5' : 'border-border'
                        }`}>
                          <RadioGroupItem value={opt.letter} id={`q${q.number}-${opt.letter}`} disabled={quizSubmitted} />
                          <Label htmlFor={`q${q.number}-${opt.letter}`} className="flex-1 cursor-pointer text-sm">
                            <span className="font-medium mr-2">{opt.letter}.</span>{opt.text}
                          </Label>
                          {optCorrect && <CheckCircle className="w-4 h-4 text-green-500" />}
                          {optWrong && <AlertCircle className="w-4 h-4 text-red-500" />}
                        </div>
                      );
                    })}
                  </RadioGroup>

                  {quizSubmitted && q.explanation && (
                    <div className="mt-4 p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
                      <HelpCircle className="w-4 h-4 inline mr-1" />
                      {q.explanation}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-8 flex justify-center gap-4">
            {!quizSubmitted ? (
              <Button onClick={handleSubmitQuiz} size="lg" data-testid="pl-submit-quiz-btn" className="bg-amber-600 hover:bg-amber-700">
                Submit Quiz
              </Button>
            ) : quizResult?.passed ? (
              <Button onClick={() => navigate('/pre-licensing/dashboard')} size="lg" data-testid="pl-continue-btn" className="bg-green-600 hover:bg-green-700">
                <Award className="w-4 h-4 mr-2" /> Continue to Dashboard
              </Button>
            ) : (
              <Button onClick={() => { setQuizSubmitted(false); setQuizAnswers({}); setQuizResult(null); }} size="lg" data-testid="pl-retry-quiz-btn" className="bg-amber-600 hover:bg-amber-700">
                Retry Quiz
              </Button>
            )}
          </div>

          {quizSubmitted && quizResult && (
            <div className={`mt-6 p-4 rounded-xl text-center ${quizResult.passed ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`} data-testid="pl-quiz-result">
              <p className="text-lg font-bold">{quizResult.passed ? 'Passed!' : 'Not Passed'}</p>
              <p className="text-sm text-muted-foreground">Score: {quizResult.score}/{quizResult.total} ({quizResult.percentage}%)</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ======== Chapter Content View ========
  return (
    <div className="min-h-screen bg-background" data-testid="pl-chapter-view">
      {/* Top Bar */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-card/95 backdrop-blur border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate('/pre-licensing/dashboard')}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="pl-back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4" />
            Dashboard
          </button>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-muted-foreground flex items-center gap-1">
              <BookOpen className="w-4 h-4" />
              Page {currentPage} of {totalPages}
            </span>
            <span className="text-muted-foreground flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {formatTime(timeSpent)}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNarrate}
              disabled={isLoadingAudio}
              data-testid="pl-narrator-btn"
              className={isNarrating ? "bg-amber-600 text-white hover:bg-amber-700" : ""}
            >
              {isLoadingAudio ? (
                <>
                  <div className="w-4 h-4 mr-2 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Loading...
                </>
              ) : isNarrating ? (
                <>
                  <VolumeX className="w-4 h-4 mr-2" />
                  Stop
                </>
              ) : (
                <>
                  <Volume2 className="w-4 h-4 mr-2" />
                  AI Narrator
                </>
              )}
            </Button>
          </div>
        </div>
        <Progress value={(currentPage / totalPages) * 100} className="h-1" />
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 pt-20 pb-32">
        {pageContent && (
          <div
            className="chapter-content prose prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: pageContent.content }}
          />
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-card/95 backdrop-blur border-t border-border z-40">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage <= 1}
            data-testid="pl-prev-page-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
          </Button>

          <span className="text-sm text-muted-foreground">
            {pageInfo?.chapter_title}
          </span>

          {isLastPage ? (
            pageInfo?.chapter_number === 7 ? (
              <Button
                onClick={async () => {
                  try {
                    await completePlChapterNoQuiz(id);
                    toast.success('Chapter completed! Course finished!');
                    navigate('/pre-licensing');
                  } catch {
                    toast.error('Failed to complete chapter');
                  }
                }}
                disabled={timerLocked}
                data-testid="pl-complete-chapter-btn"
                className={timerLocked ? "bg-gray-500 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}
              >
                {timerLocked ? (
                  <><Clock className="w-4 h-4 mr-1" /> {Math.floor(pageTimeRemaining / 60)}:{String(pageTimeRemaining % 60).padStart(2, '0')}</>
                ) : (
                  <>Complete Chapter <CheckCircle className="w-4 h-4 ml-1" /></>
                )}
              </Button>
            ) : (
              <Button
                onClick={handleStartQuiz}
                disabled={timerLocked}
                data-testid="pl-start-quiz-btn"
                className={timerLocked ? "bg-gray-500 cursor-not-allowed" : "bg-amber-600 hover:bg-amber-700"}
              >
                {timerLocked ? (
                  <><Clock className="w-4 h-4 mr-1" /> {Math.floor(pageTimeRemaining / 60)}:{String(pageTimeRemaining % 60).padStart(2, '0')}</>
                ) : (
                  <>Take Chapter Quiz <ChevronRight className="w-4 h-4 ml-1" /></>
                )}
              </Button>
            )
          ) : (
            <Button
              onClick={() => goToPage(currentPage + 1)}
              disabled={timerLocked}
              data-testid="pl-next-page-btn"
              className={timerLocked ? "bg-gray-500 cursor-not-allowed" : ""}
            >
              {timerLocked ? (
                <><Clock className="w-4 h-4 mr-1" /> {Math.floor(pageTimeRemaining / 60)}:{String(pageTimeRemaining % 60).padStart(2, '0')}</>
              ) : (
                <>Next <ChevronRight className="w-4 h-4 ml-1" /></>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreLicensingChapterPage;
